/** record_review.v2 shadow adapter (R1d) — a PURE mapping from the governed v2 field-review payload
 * (CrmRecordReviewV2Response) into the record panel's existing CrmRecordFieldState[] shape, plus the
 * record-level review context (actions + the R2b CAS token). Keeping the panel's consumed shape stable
 * means the later flip to v2 (R5-FE / R4) is a SOURCE swap, not a render rewrite.
 *
 * v1-authoritative caveat (build-plan Phase 1 / R1d): this adapter feeds the read-only SHADOW only —
 * the panel still renders from record_field_states.v1 until R5-FE. It deliberately reproduces v1's
 * status collapse (incl. `push_failed` when external_sync failed — faithful to a committed IOT overlay
 * field carrying a genuine Affinity push_error) so a shadow comparison against v1 lines up.
 *
 * The v2-only richness (allowed_actions, disposition, change_kind, reject_reason,
 * restored_from_proposal_id, review_deadline_at, annotation) is preserved verbatim on `fields` for
 * R5-FE to consume.
 *
 * ⚠ R5-FE MUST heed (adv-review R1d):
 *   1. `write_value` / `write_eligible` are INERT DEFAULTS here — never trust them to protect a field.
 *      `write_eligible: false` has never gated anything, and R5-FE must not read it as a safety property.
 *      ⚰ The original form of this warning said the panel gates the Affinity Retry button solely on
 *      `status === 'push_failed'`, so R5-FE had to withhold `onRetryPush` for a proposed / CRM-local row.
 *      That is now moot: the Pillar-D write funeral (2026-08-27) removed the Retry button, the
 *      `onRetryPush` prop and `useSyncList`, and there is no push left to protect a field from. The
 *      `push_failed` status and the `external_sync` mapping below SURVIVE — they render a stored,
 *      pre-funeral error — so keep rendering them; just do not build a retry action behind them.
 *   2. Render per-field actions from the preserved `fields[].allowed_actions`, NOT the collapsed
 *      `status`: the collapse discards the `restore` action, the no-field-Accept rule on a proposed row,
 *      and would otherwise let the panel's v1 Edit affordance fire on an accepted/dismissed field that
 *      the v2 gate does not sanction.
 */
import type {
  CrmRecordFieldState, CrmRecordReviewV2Response, RecordReviewCas, RecordReviewV2Field,
} from '../../types';

// v2 disposition → the v1 CrmRecordFieldState.status vocab (unchanged | proposed | edited |
// push_failed | accepted | dismissed). A failed external-sync takes precedence, mirroring v1's
// push_failed status (which likewise supersedes the review disposition).
const DISPOSITION_TO_STATUS: Record<string, string> = {
  pending: 'proposed',
  edited: 'edited',
  accepted: 'accepted',
  rejected: 'dismissed',
  retracted: 'dismissed',
  superseded: 'unchanged',
  unchanged: 'unchanged',
};

function statusFor(f: RecordReviewV2Field): string {
  if (f.external_sync?.status === 'failed') return 'push_failed';
  const disp = f.disposition ?? f.status ?? 'unchanged';
  return DISPOSITION_TO_STATUS[disp] ?? 'unchanged';
}

/** D4 (owner review 2026-08-21 — findings U1/U2, `docs/plans/strategic-architecture/
 * iot-panel-field-behavior-review.md` §7): is this a COMMITTED field whose proposal is RESOLVED?
 * There is no live review decision left, so the field is a plain committed field again — ONE black
 * pencil on the value in force, committed through the plain cell PATCH (never edit-and-accept, which
 * 409s on a resolved proposal: its per-proposal CAS requires `resolution IS NULL`).
 *
 * ⚠ `disposition !== 'pending'` is NOT sufficient, and this predicate exists so the container's
 * routing and ReviewFieldRow's rendering cannot answer the question differently. The server derives
 * `edited` from TWO unrelated states (`record_review._disposition`): a settled `edited_accepted`
 * resolution, AND a still-PENDING proposal carrying a live operator overlay (an edit-and-accept in
 * flight). The wire cannot tell those apart, so the discriminator is the server's own plain-edit
 * sanction — `_field_actions` appends 'edit' ONLY for a genuinely resolved committed proposal. Reading
 * it keeps `allowed_actions` authoritative (target invariant P2, finding U3) and leaves the overlay
 * case on its pre-D4 v1 row (blue dot + push Retry) instead of silently turning an in-flight
 * edit-and-accept into a plain PATCH — decision D3 is still open. */
export function isResolvedCommittedMember(f: RecordReviewV2Field, isProposedRow: boolean): boolean {
  if (isProposedRow || !f.is_member) return false;
  const disp = f.disposition;
  if (disp == null || disp === 'pending') return false;
  return (f.allowed_actions ?? []).includes('edit');
}

/** Map one v2 field into the v1-shaped CrmRecordFieldState the panel consumes. Pure. */
export function recordReviewV2FieldToState(f: RecordReviewV2Field): CrmRecordFieldState {
  return {
    field_name: f.field_name,
    label: f.label,
    field_type: f.field_type,
    status: statusFor(f),
    effective_value: f.effective_value,
    proposed_value: f.proposed_value,
    // Inert defaults — the CRM panel reads NEITHER write_value NOR write_eligible (it gates Retry on
    // status==='push_failed'; see the ⚠ R5-FE note above). write_value tracks the effective (rendered)
    // value; write_eligible is false. Do NOT treat write_eligible as a push-safety gate.
    write_value: f.effective_value,
    write_eligible: false,
    pending: (f.disposition ?? f.status) === 'pending',
    proposal_id: f.proposal_id,
    push_error: f.external_sync?.error ?? null,
  };
}

/** The panel-consumable shape produced from a record_review.v2 payload. */
export interface RecordReviewV2Adapted {
  /** v1-shaped field states for the panel's existing field grid (the shadow/source-swap target). */
  fieldStates: CrmRecordFieldState[];
  /** The raw v2 fields, preserved for R5-FE (allowed_actions / disposition / reject_reason / …). */
  fields: RecordReviewV2Field[];
  recordActions: string[];
  /** S3: the count of actionable still-`pending` committed members — what a record-level Accept-all
   * would resolve. Drives the "Accept all (N)" label; do NOT re-derive by filtering `fields` client-side
   * (that was the pre-fix bug's shape — this count and `record_actions`'s 'accept' gate must come from
   * the SAME server computation or they can disagree). */
  pendingCount: number;
  recordRevision: number;
  proposalSetHash: string;
  recordLifecycle: string;
  capabilities: { editable: boolean; proposals: boolean };
  /** The R2b record-level CAS token the panel echoes on Accept / Pass / Dismiss (stale → 409). */
  reviewCas: RecordReviewCas;
}

/** Adapt a full record_review.v2 payload into the panel-consumable shape + the record-level review
 * context (actions + CAS token). Pure — no I/O, no side effects. */
export function adaptRecordReviewV2(v2: CrmRecordReviewV2Response): RecordReviewV2Adapted {
  const fields = v2.fields ?? [];
  return {
    fieldStates: fields.map(recordReviewV2FieldToState),
    fields,
    recordActions: v2.record_actions ?? [],
    pendingCount: v2.pending_count ?? 0,
    recordRevision: v2.record_revision,
    proposalSetHash: v2.proposal_set_hash,
    recordLifecycle: v2.record_lifecycle,
    capabilities: v2.capabilities ?? { editable: false, proposals: false },
    reviewCas: { record_revision: v2.record_revision, proposal_set_hash: v2.proposal_set_hash },
  };
}
